import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Speedy DMV Services Gallery Las Vegas - Our Professional Facility',
    description: 'View our professional DMV services facility in Las Vegas Nevada. Modern office, expert staff, and efficient processing for vehicle registration, title transfer, and all Nevada DMV needs.',
    keywords: 'DMV office gallery Las Vegas, Speedy DMV facility photos, Las Vegas DMV services office Nevada',
    canonical: `${siteUrl}/gallery`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        {
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": "Speedy DMV Services - Office Gallery",
          "description": "Photos of Speedy DMV Services professional facility in Las Vegas, Nevada.",
          "url": `${siteUrl}/gallery`
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Gallery', url: '/gallery' }
        ])
      ]
    }
  });

  const categories = [
    { id: 'all', name: 'All Photos', icon: 'ri-image-line' },
    { id: 'office', name: 'Office', icon: 'ri-building-line' },
    { id: 'services', name: 'Services', icon: 'ri-service-line' },
    { id: 'customers', name: 'Customers', icon: 'ri-user-smile-line' },
    { id: 'documents', name: 'Documents', icon: 'ri-file-text-line' }
  ];

  const galleryImages = [
    {
      src: "https://public.readdy.ai/ai/img_res/9bd8886766ad0ba33f003f2a0f015917.jpg",
      alt: "DMV Office Reception Area",
      title: "Welcome Area",
      category: 'office'
    },
    {
      src: "https://readdy.ai/api/search-image?query=DMV%20service%20counter%20with%20professional%20staff%20member%20helping%20customer%20with%20vehicle%20registration%20paperwork%2C%20organized%20filing%20system%2C%20computer%20workstation%2C%20Nevada%20DMV%20forms%20and%20documents&width=400&height=300&seq=gallery2&orientation=landscape",
      alt: "Service Counter",
      title: "Professional Service",
      category: 'services'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Nevada%20vehicle%20registration%20documents%20and%20license%20plates%20display%2C%20official%20DMV%20paperwork%2C%20title%20transfer%20forms%2C%20temporary%20permits%2C%20professional%20documentation%20setup&width=400&height=300&seq=gallery3&orientation=landscape",
      alt: "DMV Documents",
      title: "Official Documents",
      category: 'documents'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Happy%20satisfied%20customers%20receiving%20their%20completed%20DMV%20paperwork%20from%20professional%20staff%20member%2C%20smiling%20faces%2C%20successful%20service%20completion%2C%20Nevada%20license%20plates%20and%20registration%20documents&width=400&height=300&seq=gallery4&orientation=landscape",
      alt: "Happy Customers",
      title: "Satisfied Customers",
      category: 'customers'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20in%20Las%20Vegas%20with%20Speedy%20DMV%20Services%20signage%2C%20professional%20business%20front%2C%20parking%20area%2C%20Nevada%20desert%20landscape%2C%20clear%20blue%20sky&width=400&height=300&seq=gallery5&orientation=landscape",
      alt: "Office Building",
      title: "Our Location",
      category: 'office'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Professional%20notary%20services%20setup%20with%20official%20stamps%2C%20legal%20documents%2C%20signing%20area%2C%20Nevada%20state%20certification%20displays%2C%20organized%20workspace%20for%20document%20authentication&width=400&height=300&seq=gallery6&orientation=landscape",
      alt: "Notary Services",
      title: "Notary Services",
      category: 'services'
    },
    {
      src: "https://readdy.ai/api/search-image?query=DMV%20staff%20member%20processing%20vehicle%20title%20transfer%20paperwork%20at%20computer%20workstation%2C%20professional%20office%20environment%2C%20Nevada%20DMV%20forms%2C%20organized%20filing%20system&width=400&height=300&seq=gallery7&orientation=landscape",
      alt: "Title Processing",
      title: "Title Transfer Process",
      category: 'services'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Customer%20consultation%20area%20with%20comfortable%20chairs%2C%20professional%20DMV%20advisor%20explaining%20paperwork%20requirements%2C%20Nevada%20vehicle%20registration%20guides%2C%20friendly%20service%20environment&width=400&height=300&seq=gallery8&orientation=landscape",
      alt: "Customer Consultation",
      title: "Expert Consultation",
      category: 'customers'
    },
    {
      src: "https://readdy.ai/api/search-image?query=Nevada%20license%20plates%20collection%20display%20showing%20various%20specialty%20plates%2C%20personalized%20plates%2C%20standard%20plates%2C%20professional%20DMV%20office%20showcase&width=400&height=300&seq=gallery9&orientation=landscape",
      alt: "License Plates Display",
      title: "License Plate Options",
      category: 'documents'
    }
  ];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              Our Professional Facility
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto opacity-90 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              Take a look at our modern, professional DMV service facility in Las Vegas
            </p>
            
            {/* Animated Camera Icons */}
            <div className="flex justify-center space-x-6 mb-8">
              {[1, 2, 3, 4].map((item, index) => (
                <div 
                  key={index}
                  className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center animate-bounce"
                  style={{animationDelay: `${index * 0.2}s`}}
                >
                  <i className="ri-camera-line text-white text-xl"></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-6 py-3 rounded-full font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredImages.map((image, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105"
                onClick={() => setSelectedImage(image.src)}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-lg mb-2">{image.title}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-white/80">
                        <i className="ri-eye-line mr-2"></i>
                        <span className="text-sm">Click to view</span>
                      </div>
                      <div className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                        <i className="ri-zoom-in-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                    {categories.find(cat => cat.id === image.category)?.name}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-image-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No images found</h3>
              <p className="text-gray-500">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-full">
            <img 
              src={selectedImage}
              alt="Gallery Image"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            
            {/* Navigation Arrows */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredImages.findIndex(img => img.src === selectedImage);
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
                setSelectedImage(filteredImages[prevIndex].src);
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = filteredImages.findIndex(img => img.src === selectedImage);
                const nextIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
                setSelectedImage(filteredImages[nextIndex].src);
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-12 text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Ready to Experience Our Service?</h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Visit our professional facility or book an appointment today. We're here to make your DMV experience fast and hassle-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button 
                onClick={() => navigate('/contact')}
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 cursor-pointer transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Appointment
              </button>
              <a 
                href="tel:702-979-7202"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-200 cursor-pointer transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 bg-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <i className="ri-information-line text-yellow-600 text-2xl mr-4 mt-1 flex-shrink-0 animate-pulse"></i>
              <div>
                <h3 className="text-lg font-bold text-yellow-800 mb-2">Important Legal Notice</h3>
                <p className="text-yellow-800 font-medium leading-relaxed">
                  I AM NOT AN ATTORNEY IN THE STATE OF NEVADA. I AM NOT AUTHORIZED TO GIVE LEGAL ADVICE OR LEGAL REPRESENTATION. I MAY NOT ACCEPT FEES FOR GIVING LEGAL ADVICE OR LEGAL REPRESENTATION.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
